/**
 * @module @semaphore-protocol/proof
 * @version 2.1.0
 * @file A library to generate and verify Semaphore proofs.
 * @copyright Ethereum Foundation 2022
 * @license MIT
 * @see [Github]{@link https://github.com/semaphore-protocol/semaphore.js/tree/main/packages/proof}
*/
'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var circomlibjs = require('circomlibjs');
var snarkjs = require('snarkjs');
var solidity = require('@ethersproject/solidity');
var strings = require('@ethersproject/strings');

/**
 * Generates a nullifier by hashing the external and the identity nullifiers.
 * @param externalNullifier The external nullifier.
 * @param identityNullifier The identity nullifier.
 * @returns The nullifier hash.
 */
function generateNullifierHash(externalNullifier, identityNullifier) {
    return circomlibjs.poseidon([BigInt(externalNullifier), BigInt(identityNullifier)]);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Hashes a signal string with Keccak256.
 * @param signal The Semaphore signal.
 * @returns The signal hash.
 */
function genSignalHash(signal) {
    return BigInt(solidity.keccak256(["bytes32"], [strings.formatBytes32String(signal)])) >> BigInt(8);
}

function generateProof(identity, group, externalNullifier, signal, snarkArtifacts) {
    if (snarkArtifacts === void 0) { snarkArtifacts = {
        wasmFilePath: "https://www.trusted-setup-pse.org/semaphore/".concat(group.depth, "/semaphore.wasm"),
        zkeyFilePath: "https://www.trusted-setup-pse.org/semaphore/".concat(group.depth, "/semaphore.zkey")
    }; }
    return __awaiter(this, void 0, void 0, function () {
        var commitment, index, merkleProof, _a, proof, publicSignals;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    commitment = identity.generateCommitment();
                    index = group.indexOf(commitment);
                    if (index === -1) {
                        throw new Error("The identity is not part of the group");
                    }
                    merkleProof = group.generateProofOfMembership(index);
                    return [4 /*yield*/, snarkjs.groth16.fullProve({
                            identityTrapdoor: identity.getTrapdoor(),
                            identityNullifier: identity.getNullifier(),
                            treePathIndices: merkleProof.pathIndices,
                            treeSiblings: merkleProof.siblings,
                            externalNullifier: externalNullifier,
                            signalHash: genSignalHash(signal)
                        }, snarkArtifacts.wasmFilePath, snarkArtifacts.zkeyFilePath)];
                case 1:
                    _a = _b.sent(), proof = _a.proof, publicSignals = _a.publicSignals;
                    return [2 /*return*/, {
                            proof: proof,
                            publicSignals: {
                                merkleRoot: publicSignals[0],
                                nullifierHash: publicSignals[1],
                                signalHash: publicSignals[2],
                                externalNullifier: publicSignals[3]
                            }
                        }];
            }
        });
    });
}

/**
 * Verifies a SnarkJS proof.
 * @param verificationKey The zero-knowledge verification key.
 * @param fullProof The SnarkJS full proof.
 * @returns True if the proof is valid, false otherwise.
 */
function verifyProof(verificationKey, _a) {
    var proof = _a.proof, publicSignals = _a.publicSignals;
    return snarkjs.groth16.verify(verificationKey, [
        publicSignals.merkleRoot,
        publicSignals.nullifierHash,
        publicSignals.signalHash,
        publicSignals.externalNullifier
    ], proof);
}

/**
 * Makes a proof compatible with the Verifier.sol method inputs.
 * @param proof The proof generated with SnarkJS.
 * @returns The Solidity compatible proof.
 */
function packToSolidityProof(proof) {
    return [
        proof.pi_a[0],
        proof.pi_a[1],
        proof.pi_b[0][1],
        proof.pi_b[0][0],
        proof.pi_b[1][1],
        proof.pi_b[1][0],
        proof.pi_c[0],
        proof.pi_c[1]
    ];
}

exports.generateNullifierHash = generateNullifierHash;
exports.generateProof = generateProof;
exports.generateSignalHash = genSignalHash;
exports.packToSolidityProof = packToSolidityProof;
exports.verifyProof = verifyProof;
